import styled, { css } from 'styled-components';
import Badge from '../../../_shared/components/Badge';
import applyResponsiveVdpStyles from '../../../_shared/helpers/applyResponsiveVdpStyles';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

const isOmp = isOmpPlatform();

/**
 * Variant Definitions
 */
const StyledVdpWrapperVariants = {
  mini: css`
    left: 0;
    min-width: 100%;
    height: max-content;
    box-shadow: none;
    padding-bottom: 42px;
  `,
  expanded: css`
    margin: 18px 0 9px;
    z-index: 1;
  `
};

const stickyNav = css`
  top: ${isOmp ? '66px' : '90px'};
  animation: moveDown 0.5s ease-in-out;
`;

const StyledVdpNavVariants = {
  mini: {
    div: css`
      display: flex;
      justify-content: center;
      width: 100%;
      max-height: 104px;
      margin: 0 0 24px 0;
    `,
    ul: css`
      flex-wrap: wrap;
      flex-direction: row;
      padding: 0 18px;
    `,
    li: css`
      width: calc(50% - 6px);
      max-height: 40px;
      height: 40px;
      padding: 0 12px;
      margin-bottom: 12px;
      box-shadow: ${isOmp ? 'none' : '0 0 6px rgba(0, 0, 0, 0.2)'};
      border: ${({ theme }) =>
        isOmp ? `1px solid ${theme.COLORS.NEUTRAL_110}` : 'none'};
      border-radius: ${isOmp ? 3 : 0}px;

      &:nth-child(even) {
        margin-right: 12px;
      }

      &:first-of-type {
        display: none;
        order: 6;
      }
    `,
    a: css`
      display: flex;
      justify-content: space-between;
      font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
    `
  },
  expanded: {
    div: css`
      position: fixed;
      top: 0;
      transition: top 0.3s ease-in-out;
      background-color: ${({ theme }) => theme.COLORS.BASE};

      box-shadow: 0px 6px 6px rgba(0, 0, 0, 0.2);

      ul {
        width: ${({ theme }) => theme.BP.VDP_MOBILE_WH};
        margin: auto;
        padding: 0 24px;
      }

      ${({ sticky }) => sticky && stickyNav}
    `,
    a: css`
      &.active {
        color: ${({ theme }) => theme.COLORS.BASE_INVERTED};

        &#nav-transport + .transport-underline {
          display: block;
        }
      }
    `
  }
};

const VdpNavUnderlineVariants = {
  mini: css`
    display: none;
  `
};

/**
 * Styled Components
 */

export const StyledVdpWrapper = styled.div`
  position: relative;
  width: 1146px;
  height: 54px;
  background-color: ${props =>
    props.sticky
      ? ({ theme }) => theme.COLORS.TRANSPARENT
      : ({ theme }) => theme.COLORS.BASE};
  box-shadow: ${props =>
    props.sticky ? 'none' : '0px 1px 6px rgba(0, 0, 0, 0.2)'};
  display: ${props => (props.sticky ? 'block' : 'none')};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledVdpWrapperVariants.mini,
      expanded: StyledVdpWrapperVariants.expanded
    })}
`;

export const StyledVdpNav = styled.div`
  width: ${({ theme }) => theme.BP.VDP_DESKTOP_WL};
  margin: 0 auto;
  height: 100%;
  max-height: 54px;
  z-index: 2;
  && {
    ul {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 100%;
      position: relative;
      padding: 0 12px;
      ${({ theme }) =>
        applyResponsiveVdpStyles(theme, {
          mini: StyledVdpNavVariants.mini.ul
        })}
      li {
        font-size: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
        background: ${({ theme }) => theme.COLORS.TRANSPARENT};
        cursor: pointer;
        height: 100%;
        max-height: 54px;
        padding: 0 24px;
        ${({ theme }) =>
          applyResponsiveVdpStyles(theme, {
            mini: StyledVdpNavVariants.mini.li
          })}

        &:hover {
          background: ${({ theme }) => theme.COLORS.NEUTRAL_5};
        }

        a {
          display: flex;
          justify-content: center;
          align-items: center;
          color: ${({ theme }) => theme.COLORS.NEUTRAL_30};
          height: 100%;

          &:focus {
            outline: -webkit-focus-ring-color auto 1px;

            &:not(:focus-visible) {
              outline: none;
            }
          }

          body.using-mouse &:focus {
            outline: none;
          }
          ${({ theme }) =>
            applyResponsiveVdpStyles(theme, {
              mini: StyledVdpNavVariants.mini.a,
              expanded: StyledVdpNavVariants.expanded.a
            })}
        }
      }
    }
  }
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledVdpNavVariants.mini.div,
      expanded: StyledVdpNavVariants.expanded.div
    })}
`;

export const StyledBadge = styled(Badge)`
  border: 2px solid ${({ theme }) => theme.COLORS.ORANGE};
  color: ${({ theme }) => theme.COLORS.ORANGE};
  margin-left: 6px;
  min-width: 36px;
  border-radius: 18px;
  text-align: center;
`;

export const VdpNavUnderline = styled.span`
  position: absolute;
  bottom: 0;
  left: ${props => props.underline.x || 0}px;
  min-width: ${props => props.underline.width || 80}px;
  height: 2px;
  background: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  transition: all 0.35s ease-in-out;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: VdpNavUnderlineVariants.mini
    })}
`;
